import {
	ConfidenceLabel,
	Recommendation,
	RepairReplaceBands,
} from '../../types/asset_replacement_score';

export type ActiveZone = Exclude<Recommendation, 'insufficient_data'>;
export type ConfidenceTier = 'low' | 'medium' | 'high';

/**
 * Bar + chip fill colour by (zone × confidence tier).
 * Values are raw hex / rgba (not in our Tailwind palette) and are applied
 * via inline `style={{ backgroundColor }}`. Low confidence intentionally
 * collapses all zones to grey so the viewer doesn't over-weight a signal
 * the backend wasn't confident in.
 */
export const BAR_COLOR: Record<ActiveZone, Record<ConfidenceTier, string>> = {
	repair: {
		low: '#D9D9D9', // grey-5
		medium: '#52C41A80', // green 50%
		high: '#52C41A', // green
	},
	monitor: {
		low: '#D9D9D9',
		medium: 'rgba(250, 173, 20, 0.5)',
		high: '#FAAD14',
	},
	replace: {
		low: '#D9D9D9',
		medium: 'rgba(255, 77, 79, 0.5)',
		high: '#FF4D4F',
	},
};

export const confidenceTier = (label: ConfidenceLabel): ConfidenceTier => {
	if (label === 'high') return 'high';
	if (label === 'medium') return 'medium';
	return 'low';
};

export const zoneFromScore = (score: number, bands: RepairReplaceBands): ActiveZone => {
	if (score < bands.repair[1]) return 'repair';
	if (score < bands.monitor[1]) return 'monitor';
	return 'replace';
};

/**
 * Chip tint: dedicated status-token palette per Figma. Separate from the
 * bar palette — chips use pale backgrounds with matching dark text.
 * Saturation does not vary by confidence (only the progress bar does).
 */
export const CHIP_COLOR: Record<ActiveZone, { backgroundColor: string; color: string }> = {
	repair: { backgroundColor: '#E1FBE7', color: '#006306' },
	monitor: { backgroundColor: '#FFF4D6', color: '#A07600' },
	replace: { backgroundColor: '#F9E6E7', color: '#A32828' },
};

export const zoneChipStyle = (zone: ActiveZone): { backgroundColor: string; color: string } =>
	CHIP_COLOR[zone];
